import React, { useEffect, useCallback, useMemo } from 'react';
import { useInputHelper, MultiSelectInput } from 'framework/form';
import { usePeopleManagementApi } from 'infrastructure/api';
import useEmployeeSuggestionsService from '../employeeSuggestionsService';

interface EmployeesInputProps {
  customMapper?: (item: any) => { value: string; label: string };
  defaultFilter?: Record<string, any> | { filter: any[] };
  isAdvanced?: boolean;
  params?: Record<string, any>;
  enableAvailabilitySkillsFilter?: boolean;
  withContractors?: boolean;
  source: string;
  [key: string]: any;
}

const EmployeesInput: React.FC<EmployeesInputProps> = ({
  customMapper,
  defaultFilter = {},
  isAdvanced = false,
  enableAvailabilitySkillsFilter = false,
  withContractors = false,
  params,
  ...props
}) => {
  const {
    searchAll: searchEmployeeSuggestions,
    searchAllWithContractors: searchEmployeeSuggestionsWithContractors,
    searchAllAdvanced: searchAdvancedEmployeeSuggestions,
  } = useEmployeeSuggestionsService();
  const { getValue, initializeValue } = useInputHelper(props);
  const { sendPostRequest } = usePeopleManagementApi();

  const getEmployeeSuggestions = useCallback(
    async (query: any) => {
      if (isAdvanced) {
        const searchStringFilter = {
          filterType: 'FILTER',
          property: 'searchString',
          operator: 'CT',
          value: query,
        };

        const advancedFilter = {
          filterType: 'FILTER_GROUP',
          condition: 'AND',
          filters: [
            ...(defaultFilter && 'filter' in defaultFilter ? defaultFilter.filter : []),
            searchStringFilter,
          ].filter(Boolean),
        };

        return searchAdvancedEmployeeSuggestions(advancedFilter);
      }

      const basicFilter = {
        searchString: { ct: query },
        ...defaultFilter,
      };

      const searchMethod = withContractors ? searchEmployeeSuggestionsWithContractors : searchEmployeeSuggestions;

      return searchMethod({
        filter: {
          ...basicFilter,
        },
      });
    },
    [
      isAdvanced,
      defaultFilter,
      searchEmployeeSuggestions,
      searchEmployeeSuggestionsWithContractors,
      searchAdvancedEmployeeSuggestions,
      withContractors,
    ],
  );

  const filterByAvailabilitySkills = useCallback(
    async (employeeSuggestions: any[]) => {
      if (!enableAvailabilitySkillsFilter || employeeSuggestions.length === 0) {
        return employeeSuggestions;
      }

      try {
        const request = await sendPostRequest('/employee-availability-skills', {
          specifiedEmployees: employeeSuggestions.map(it => it.alias),
          specialization: null,
          technology: null,
          ...params,
        });

        const interviewSkilledEmployeeAliases: { employeeAlias: string }[] = await request.json();
        const filteredAliases = interviewSkilledEmployeeAliases.map(it => it.employeeAlias);

        return employeeSuggestions.filter(suggestion => filteredAliases.includes(suggestion.alias));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error filtering by availability skills:', error);
        return employeeSuggestions;
      }
    },
    [enableAvailabilitySkillsFilter, sendPostRequest, params],
  );

  const getInterviewerEmployeeSuggestions = useCallback(
    async (query: any) => {
      const employeeSuggestions = await getEmployeeSuggestions(query);
      return filterByAvailabilitySkills(employeeSuggestions);
    },
    [getEmployeeSuggestions, filterByAvailabilitySkills],
  );

  const defaultMapper = useCallback(
    (item: any) => ({
      value: item.alias,
      label: `${item.nameEn?.firstName ?? ''} ${item.nameEn?.lastName ?? ''} (${item.workingEmail ?? '—'})`,
    }),
    [],
  );

  const mapEntry = customMapper || defaultMapper;

  const formatData = useCallback((selectData: any[]) => selectData.map(mapEntry), [mapEntry]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rawValue = getValue() || [];

  const value = useMemo(() => {
    if (Array.isArray(rawValue)) {
      return rawValue.map((item: any) => (item.value ? item : mapEntry(item)));
    }

    if (rawValue && typeof rawValue === 'object' && 'value' in rawValue) {
      return rawValue;
    }

    if (rawValue) {
      return mapEntry(rawValue);
    }

    return [];
  }, [rawValue, mapEntry]);

  const noOptionsMessage = useCallback(
    ({ inputValue }: any) => (inputValue.length ? 'Employees not found' : 'Start typing the name'),
    [],
  );

  useEffect(() => {
    initializeValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MultiSelectInput
      {...props}
      noOptionsMessage={noOptionsMessage}
      value={value}
      loadSuggestionsMethod={
        enableAvailabilitySkillsFilter ? getInterviewerEmployeeSuggestions : getEmployeeSuggestions
      }
      mapResults={formatData}
    />
  );
};

export default EmployeesInput;
